import React from 'react'
import SequenceInfo from './SequenceInfo'
import CircleLoader from '@src/components/CircleLoader'
import OptionalModifications from '@src/routes/Configurators/components/OptionalModifications'
import SequenceProperties from './SequenceProperties'
import Specifications from './Specifications'
import Button from '@material-ui/core/Button'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../messages'
import {
  useTubeTotalValidationMutation,
  useTubePriceCallMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import { ActiveLinkType, TubeProperties, TubeProduct } from '../enum'
import { configContext } from '../../../utils/StandardReducer'
import { standardTubeRoute, useRouter } from '@src/routes'
import { makeStyles, Theme } from '@material-ui/core/styles'
import AquaSpecifications from './AquaSpecification'
import ViewAllDialog from '@src/routes/Configurators/components/ViewAllDialog'
import SpecificationsAndShipping from './SpecifiactionsAndShipping'
import { ConfiguratorValues } from '../../../utils/constants'

const useStyles = makeStyles((theme: Theme) => {
  return {
    viewAll: {
      marginLeft: theme.spacing(10),
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      '&:hover': {
        backgroundColor: 'inherit',
        color: theme.palette.primary.dark,
      },
      fontWeight: theme.typography.fontWeightMedium,
      width: theme.spacing(50),
    },
    continue: {
      float: 'right',
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      width: theme.spacing(50),
      fontWeight: theme.typography.fontWeightMedium,
    },
    AddOligoButton: {
      minWidth: theme.spacing(50),
      fontWeight: theme.typography.fontWeightMedium,
    },
    viewAllButton: {
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(5),
      minWidth: theme.spacing(50),
      fontWeight: theme.typography.fontWeightMedium,
    },
    continueButton: {
      float: 'right',
      marginTop: theme.spacing(1),
      minWidth: theme.spacing(50),
      fontWeight: theme.typography.fontWeightMedium,
    },
    configDefaultButton: {
      width: theme.spacing(50),
      fontWeight: theme.typography.fontWeightMedium,
    },
  }
})

const SingleProductEntry = (props) => {
  const {
    specificationsData,
    productType,
    productDetails,
    setActive,
    setErrorMsg,
    setItemDetails,
  } = props
  const [tubeTotalValidation, { loading: validationLoading }] =
    useTubeTotalValidationMutation()
  const [tubePriceCall, { loading: priceLoading }] = useTubePriceCallMutation()
  const { state, dispatch } = React.useContext(configContext)
  const {
    sessionData,
    callTotalValidation,
    currentOligoIndex,
    OligoIndex,
    callOverrideValidation,
    applyToAllTotalValidation,
  } = state
  const [sequencePreviewData, setSequencePreviewData] = React.useState({
    sequence: '--',
    baseCount: '--',
    molecularWeight: '--',
    secondaryStructure: '--',
    primerDimer: '--',
    meltingTemperature: '--',
    gcContent: '--',
  })
  const classes = useStyles()
  const router = useRouter()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const intl = useIntl()
  const maxItems =
    productDetails.productType === TubeProduct.iscaledna
      ? TubeProperties.MaximumOligosAllowedIscaledna
      : TubeProperties.MaximumOligosAllowed

  const product = router?.query?.product ?? ''

  const orderPath = `${standardTubeRoute.index()}?product=${product}&activeLink=${
    ActiveLinkType.ConfirmOrder
  }`

  const handleApplyToAllTotalValidation = async () => {
    const response: any = await tubeTotalValidation({
      variables: { input: sessionData, productInput: productDetails },
    })
    if (!response.data.tubeTotalValidation[0].serverError) {
      response.data.tubeTotalValidation.forEach((item, index) => {
        sessionData[index].epsilon = item.epsilon
        if (item.listErrors.length !== 0) {
          item.listErrors.forEach((err) => {
            delete err.__typename
          })
          if (sessionData.length > index) {
            sessionData[index].listError = item.listErrors
          }
        } else {
          sessionData[index].listError = []
        }
      })
      dispatch({
        type: 'UPDATE_INPUT',
        data: [...state.sessionData],
      })
      dispatch({ type: 'UPDATE_APPLY_TO_ALL_TOTAL_VALIDATION', data: false })
    }
  }

  const totalValidation = async () => {
    const response: any = await tubeTotalValidation({
      variables: { input: sessionData, productInput: productDetails },
    })
    if (response.data && response.data.tubeTotalValidation.length) {
      if (!response.data.tubeTotalValidation[0].serverError) {
        let isValidData = true
        response.data.tubeTotalValidation.forEach((item, index) => {
          sessionData[index].epsilon = item.epsilon
          if (item.listErrors.length !== 0) {
            const error: any[] = []
            item.listErrors.forEach((err) => {
              delete err.__typename
              error.push(err)
            })
            if (sessionData.length > index) {
              sessionData[index].listError = error
            }
            if (callOverrideValidation) {
              dispatch({ type: 'IS_VALID_TOTAL_VALIDATION', data: false })
            } else {
              dispatch({ type: 'IS_VALID_TOTAL_VALIDATION', data: false })
              //TODO:Remove commented code if below code is not required.Commenting as a fix for STRAT-11563.
              /* dispatch({
              //   type: 'UPDATE_OLIGO_INDEX',
              //   data: index,
               })*/

              dispatch({
                type: 'IS_RESET_ERROR',
                data: false,
              })
              if (isValidData) {
                //if there are multiple error, current oligo index should be the first error item
                dispatch({
                  type: 'UPDATE_CURRENT_OLIGO_INDEX',
                  data: index,
                })
                dispatch({
                  type: 'UPDATE_PAGE_LOAD',
                  data: 1,
                })
              }
            }
            isValidData = false
          }
        })

        dispatch({
          type: 'UPDATE_TOTALVALIDATION',
          data: response.data.tubeTotalValidation[currentOligoIndex],
        })
        dispatch({
          type: 'TOTAL_VALIDATION_DATA',
          data: response.data.tubeTotalValidation,
        })

        if (isValidData) {
          if (
            response.data.tubeTotalValidation[currentOligoIndex].listErrors
              .length === 0
          ) {
            dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: false })
            dispatch({
              type: 'IS_VIEW_ALL_CLICK',
              data: false,
            })
            if (callOverrideValidation) {
              dispatch({ type: 'IS_VALID_TOTAL_VALIDATION', data: true })
              dispatch({ type: 'IS_DISPLAY_ERROR', data: false })
              setErrorMsg('')
            } else {
              dispatch({ type: 'IS_DISPLAY_ERROR', data: false })
              setErrorMsg('')
              setSequencePreviewData({
                sequence: intl.formatMessage(
                  messages.CONF_SEQUENCE_PREVIEW_HELP_TEXT
                ),
                baseCount: '--',
                molecularWeight: '--',
                secondaryStructure: '--',
                primerDimer: '--',
                meltingTemperature: '--',
                gcContent: '--',
              })
              dispatch({ type: 'IS_VALID_TOTAL_VALIDATION', data: true })
              dispatch({ type: 'ADD_NEW_OLIGO', data: state.oligoCount + 1 })
              dispatch({ type: 'IS_LABEL_AMINO_ACID_ERROR', data: false })
            }
          }
          dispatch({
            type: 'IS_COMING_FROM_REVIEW_PAGE',
            data: false,
          })
          dispatch({
            type: 'IS_CONCENTRATION_ENABLE',
            data: false,
          })
        } else {
          if (callOverrideValidation) {
            dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: true })
            dispatch({ type: 'SET_ERROR_ICON', data: true })
          } else {
            dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: true })
            dispatch({ type: 'IS_DISPLAY_ERROR', data: true })
            setErrorMsg(
              intl.formatMessage(
                messages.SINGLE_PRODUCT_ENTRY_PAGE_LEVEL_ERROR_MESSAGE
              )
            )
            dispatch({ type: 'SET_ERROR_ICON', data: true })
          }
        }
      } else {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: ConfiguratorValues.InternalServerError,
        })
      }
    } else {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: ConfiguratorValues.InternalServerError,
      })
    }
    dispatch({ type: 'CALL_TOTAL_VALIDATION', data: false })
    dispatch({ type: 'SPECIFICATION_TOTAL_VALIDATION', data: true })
    if (!callOverrideValidation) {
      window.scrollTo(0, 0)
    }
  }

  React.useEffect(() => {
    if (callTotalValidation) {
      totalValidation()
    }
    if (applyToAllTotalValidation) {
      handleApplyToAllTotalValidation()
    }
    // TODO: need a test before we refactor these dependencies
  }, [callTotalValidation, applyToAllTotalValidation])

  const priceCall = async () => {
    if (
      sessionData.length <=
      (productDetails.productType === TubeProduct.iscaledna
        ? TubeProperties.MaximumOligosAllowedIscaledna
        : TubeProperties.MaximumOligosAllowed)
    ) {
      const response: any = await tubePriceCall({
        variables: { input: sessionData, productInput: productDetails },
      })
      if (response && !response.data.tubePriceCall.result[0].serverError) {
        const error: any[] = []
        dispatch({
          type: 'CALL_RULE_VALIDATION',
          data: true,
        })
        response.data.tubePriceCall.result.forEach((resp) => {
          resp.listErrors.forEach((item) => {
            delete item.__typename
          })
          error.push(resp.listErrors)
        })

        let isValidData = true
        setItemDetails(response.data.tubePriceCall.result)
        response.data.tubePriceCall.result.forEach((item, index) => {
          if (item.listErrors.length !== 0) {
            if (isValidData) {
              //if there are multiple error, current oligo index should be the first error item
              dispatch({
                type: 'UPDATE_CURRENT_OLIGO_INDEX',
                data: index,
              })
            }
            dispatch({
              type: 'CALL_RULE_VALIDATION',
              data: false,
            })

            dispatch({
              type: 'UPDATE_PAGE_LOAD',
              data: 1,
            })
            isValidData = false
          } else {
            sessionData[index].price = item.price
            sessionData[index].priceHeaderError = item.priceHeaderError
            sessionData[index].currencySymbol = item.currencySymbol
            sessionData[index].currency = item.currency
            sessionData[index].epsilon = item.epsilon
          }
        })

        dispatch({ type: 'UPDATE_TOTALVALIDATION', data: error })
        dispatch({
          type: 'TOTAL_VALIDATION_DATA',
          data: response.data.tubePriceCall.result,
        })
        if (isValidData) {
          dispatch({
            type: 'IS_COMING_FROM_REVIEW_PAGE',
            data: false,
          })
          dispatch({
            type: 'IS_CONCENTRATION_ENABLE',
            data: false,
          })
          router.push(orderPath)
          dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: false })
          dispatch({
            type: 'IS_VIEW_ALL_CLICK',
            data: false,
          })
          dispatch({ type: 'IS_DISPLAY_ERROR', data: false })
          setErrorMsg('')
          dispatch({ type: 'IS_CONFIRM_ORDER', data: true })

          dispatch({ type: 'IS_BULK_REVIEW', data: false })
          dispatch({ type: 'SET_STEP_NO', data: 1 })
          if (sessionData) {
            dispatch({
              type: 'UPDATE_INPUT',
              data: [...sessionData],
            })
          }
        } else {
          dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: true })
          dispatch({ type: 'SET_STEP_NO', data: 0 })
          dispatch({ type: 'IS_DISPLAY_ERROR', data: true })
          setErrorMsg(intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2))

          dispatch({ type: 'SET_ERROR_ICON', data: true })
          window.scrollTo(0, 0)
        }
        dispatch({ type: 'SPECIFICATION_TOTAL_VALIDATION', data: true })
      } else {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: ConfiguratorValues.InternalServerError,
        })
        window.scrollTo(0, 0)
      }
      dispatch({
        type: 'ON_CONTINUE_BUTTON_PRICECALL',
        data: true,
      })
    } else {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.CONF_MAX_ITEMS_ALLOWED, { maxItems }),
      })
      window.scrollTo(0, 0)
    }
  }
  const addOligo = () => {
    if (
      sessionData.length <
      (productDetails.productType === TubeProduct.iscaledna
        ? TubeProperties.MaximumOligosAllowedIscaledna
        : TubeProperties.MaximumOligosAllowed)
    ) {
      dispatch({ type: 'CALL_OVERRIDE_VALIDATION', data: false })
      dispatch({ type: 'IS_CONFIRM_ORDER', data: false })
      dispatch({ type: 'CALL_TOTAL_VALIDATION', data: true })
      dispatch({
        type: 'IS_ADD_OLIGO',
        data: true,
      })
    } else {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.CONF_MAX_ITEMS_ALLOWED, { maxItems }),
      })
      window.scrollTo(0, 0)
    }
  }
  const performTubeDelete = (item, type) => {
    let deleteAll = false
    if (type === 'Selected' || type === 'SelectedViewAll') {
      if (OligoIndex !== 0) {
        const newIndex = OligoIndex - 1
        const array = [...state.sessionData]
        const index = array.indexOf(item)
        let deletedIndex = index
        if (index !== -1) {
          sessionData.splice(index, 1)
        }

        if (index > sessionData.length || index === sessionData.length) {
          deletedIndex = newIndex
        }
        dispatch({
          type: 'UPDATE_OLIGO_INDEX',
          data: newIndex,
        })
        dispatch({
          type: 'UPDATE_CURRENT_OLIGO_INDEX',
          data: deletedIndex,
        })
        dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: true })
        dispatch({
          type: 'CALL_SESSION_API',
          data: true,
        })
        dispatch({ type: 'IS_CONFIRM_ORDER', data: false })

        dispatch({
          type: 'UPDATE_INPUT',
          data: [...state.sessionData],
        })
        deleteAll = false
      } else {
        deleteAll = true
      }
    } else if (type === 'All') {
      deleteAll = true
    }

    if (deleteAll) {
      const emptyStateObj = {
        additionalCarrierProtein: 'None',
        additionalComments: '',
        additionalServices: [],
        amount: '',
        applyToAll: false,
        autoValidation: '',
        baseCount: 0,
        carrierProtein: '',
        cartId: '',
        comments: '',
        concentration: '',
        conjugation: 'None',
        conjugationAmount: '',
        conjugationChemistry: '',
        countryCode: '',
        crossContamination: '',
        crRnaType: '',
        cterminalMod: '',
        currency: '',
        currencySymbol: '',
        customPeptideType: '',
        epsilon: 0,
        fiveMod: '',
        fiveModCategory: '',
        flag: 0,
        format: 'DRY',
        fullSOligoBackbone: false,
        gcContent: '',
        id: '1',
        internalModCategory: '',
        isLabelledAminoAcid: true,
        isoCode: false,
        isOverridden: false,
        itemId: '',
        listError: [
          {
            key: '',
            value: '',
          },
        ],
        material: '',
        meltingTemparature: '',
        minimumYield: '',
        molecularWeight: '',
        name: '',
        nterminalMod: '',
        oligoId: 1,
        orderItemId: '',
        otherValue: '',
        packageType: '',
        plateType: '',
        price: '',
        primerDimer: '',
        productType: '',
        purification: '',
        purityLevel: '',
        quantitation: '',
        scale: '',
        secondaryStructure: '',
        sequence: '',
        sequenceLocation: '',
        threeMod: '',
        threeModCategory: '',
        tubes: '1',
      }
      dispatch({
        type: 'UPDATE_OLIGO_INDEX',
        data: 0,
      })
      dispatch({
        type: 'UPDATE_CURRENT_OLIGO_INDEX',
        data: 0,
      })
      dispatch({
        type: 'IS_COMING_FROM_REVIEW_PAGE',
        data: false,
      })
      dispatch({
        type: 'IS_CONCENTRATION_ENABLE',
        data: false,
      })
      dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: false })
      dispatch({
        type: 'IS_VIEW_ALL_CLICK',
        data: false,
      })
      dispatch({
        type: 'UPDATE_INPUT',
        data: [emptyStateObj],
      })
      dispatch({
        type: 'CALL_SESSION_API',
        data: true,
      })
      dispatch({ type: 'FOCUS_ON_SEQUENCEBOX', data: false })
      dispatch({ type: 'UPDATE_OPTIONAL_BASES', data: '' })
      dispatch({ type: 'UPDATE_AQUA_INTERNAL_MOD', data: '' })
      dispatch({ type: 'IS_DELETE_ALL', data: true })
    }

    dispatch({
      type: 'UPDATE_PAGE_LOAD',
      data: 1,
    })
    dispatch({
      type: 'IS_DELETE_PERFORM',
      data: true,
    })
    dispatch({
      type: 'IS_RESET_ERROR',
      data: true,
    })
    dispatch({
      type: 'SPECIFICATION_TOTAL_VALIDATION',
      data: false,
    })
    dispatch({ type: 'IS_DISPLAY_ERROR', data: false })
    if (deleteAll) {
      setDialogOpen(false)
      dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: false })
      dispatch({
        type: 'IS_VIEW_ALL_CLICK',
        data: false,
      })
      dispatch({ type: 'CONFIRM_ORDER', data: false })
      dispatch({ type: 'IS_CONFIRM_ORDER', data: false })
    }
    window.scrollTo(0, 0)
  }

  // This method is use to perform delete and delete all operations
  const deleteOligoItem = (item, type) => {
    dispatch({ type: 'IS_TUBE_ITEM_DELETE', data: true })
    performTubeDelete(item, type)
  }

  React.useEffect(() => {
    if (
      sessionData &&
      sessionData.length === 1 &&
      sessionData[0].name === '' &&
      sessionData[0].sequence === '' &&
      !productDetails.isCSRSupervisor
    ) {
      window.scrollTo(0, 0)
    }
    if (state.isPageLoad === 1) {
      window.scrollTo(0, 0)
    }
  }, [sessionData[state.currentOligoIndex]])

  React.useEffect(() => {
    return function cleanupError() {
      setErrorMsg('')
      if (
        sessionData &&
        sessionData.length === 1 &&
        sessionData[0].name === '' &&
        sessionData[0].sequence === ''
      ) {
        dispatch({ type: 'SPECIFICATION_TOTAL_VALIDATION', data: false })
        dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: false })
        dispatch({ type: 'SET_ERROR_ICON', data: false })
      } else {
        //added for STRAT-16385 fix
        dispatch({ type: 'IS_VALID_TOTAL_VALIDATION', data: false })
      }
      //added for STRAT-16385 fix
      dispatch({
        type: 'UPDATE_PAGE_LOAD',
        data: 1,
      })
    }
  }, [])

  const specificationComponent = () => {
    if (productDetails.productType === TubeProduct.Aqua) {
      return (
        <div>
          <AquaSpecifications
            specificationsData={specificationsData}
            productDetails={productDetails}
          />
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.configDefaultButton}
            onClick={addOligo}
          >
            <FormattedMessage {...messages.CONF_ADD_PEPTIDE} />
          </Button>
          <Button
            color="primary"
            variant="outlined"
            size="large"
            className={classes.viewAll}
            onClick={() => setDialogOpen(true)}
            id="tubes-view-all-button"
            data-testid="tubes-view-all-button"
          >
            <FormattedMessage {...messages.CONF_VIEW_ALL_SIDE_BAR} />
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.continue}
            onClick={priceCall}
            id="tubes-continue-button"
            data-testid="tubes-continue-button"
          >
            <FormattedMessage {...messages.CONF_AQUA_CONTINUE_BUTTON} />
          </Button>
        </div>
      )
    } else if (productDetails.productType === TubeProduct.Cpeptide) {
      return (
        <div>
          <Specifications
            specificationsData={specificationsData}
            productDetails={productDetails}
          />
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.configDefaultButton}
            onClick={addOligo}
          >
            <FormattedMessage {...messages.CONF_ADD_PEPTIDE} />
          </Button>
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={() => setDialogOpen(true)}
            className={classes.viewAll}
            id="tubes-view-all-button"
            data-testid="tubes-view-all-button"
          >
            <FormattedMessage {...messages.CONF_VIEW_ALL_SIDE_BAR} />
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.continue}
            onClick={priceCall}
            id="tubes-continue-button"
            data-testid="tubes-continue-button"
          >
            <FormattedMessage {...messages.CONF_AQUA_CONTINUE_BUTTON} />
          </Button>
        </div>
      )
    } else {
      return (
        <div>
          <Specifications
            specificationsData={specificationsData}
            productDetails={productDetails}
          />
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.configDefaultButton}
            onClick={addOligo}
            id="tubes-add-oligo-button"
            data-testid="tubes-add-oligo-button"
          >
            <FormattedMessage {...messages.CONF_ADD_OLIGO_SIDE_BAR} />
          </Button>
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={() => setDialogOpen(true)}
            className={classes.viewAll}
            id="tubes-view-all-button"
            data-testid="tubes-view-all-button"
          >
            <FormattedMessage {...messages.CONF_VIEW_ALL_SIDE_BAR} />
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.continue}
            onClick={priceCall}
            id="tubes-continue-button"
            data-testid="tubes-continue-button"
          >
            <FormattedMessage {...messages.CONF_AQUA_CONTINUE_BUTTON} />
          </Button>
          <ViewAllDialog
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            viewAllItems={sessionData}
            setActive={setActive}
            onDelete={deleteOligoItem}
            productName={productType}
          />
        </div>
      )
    }
  }

  if (
    productType === TubeProduct.EasyOligo ||
    productType === TubeProduct.PureSimple
  ) {
    return (
      <>
        <SequenceInfo
          productDetails={productDetails}
          setSequencePreviewData={setSequencePreviewData}
        />
        <SequenceProperties
          productDetails={productDetails}
          sequencePreviewData={sequencePreviewData}
        />
        <SpecificationsAndShipping
          specificationsData={specificationsData}
          productType={productType}
        />
        <Button
          color="primary"
          variant="contained"
          size="large"
          className={classes.configDefaultButton}
          onClick={addOligo}
          id="tubes-add-oligo-button"
          data-testid="tubes-add-oligo-button"
        >
          <FormattedMessage {...messages.CONF_ADD_OLIGO_SIDE_BAR} />
        </Button>
        <Button
          color="primary"
          variant="outlined"
          size="large"
          className={classes.viewAll}
          onClick={() => setDialogOpen(true)}
          id="tubes-view-all-button"
          data-testid="tubes-view-all-button"
        >
          <FormattedMessage {...messages.CONF_VIEW_ALL_SIDE_BAR} />
        </Button>
        <Button
          color="primary"
          variant="contained"
          size="large"
          className={classes.continue}
          onClick={priceCall}
          id="tubes-continue-button"
          data-testid="tubes-continue-button"
        >
          <FormattedMessage {...messages.CONF_AQUA_CONTINUE_BUTTON} />
        </Button>
        <ViewAllDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          viewAllItems={sessionData}
          setActive={setActive}
          onDelete={deleteOligoItem}
          productName={productType}
        />
        {priceLoading && <CircleLoader />}
        {validationLoading && <CircleLoader />}
      </>
    )
  }
  if (productDetails.productType === TubeProduct.Wellred) {
    return (
      <div>
        <SequenceInfo
          productDetails={productDetails}
          setSequencePreviewData={setSequencePreviewData}
        />
        <OptionalModifications
          modificationResponseData={specificationsData}
          productDetails={productDetails}
        />
        <SequenceProperties
          productDetails={productDetails}
          sequencePreviewData={sequencePreviewData}
        />
        <Specifications
          specificationsData={specificationsData}
          productDetails={productDetails}
        />
        <Button
          color="primary"
          variant="contained"
          size="large"
          className={classes.AddOligoButton}
          onClick={addOligo}
          id="tubes-add-oligo-button"
          data-testid="tubes-add-oligo-button"
        >
          <FormattedMessage {...messages.CONF_ADD_OLIGO_SIDE_BAR} />
        </Button>

        <Button
          color="primary"
          variant="outlined"
          size="large"
          className={classes.viewAllButton}
          onClick={() => setDialogOpen(true)}
          id="tubes-view-all-button"
          data-testid="tubes-view-all-button"
        >
          <FormattedMessage {...messages.CONF_VIEW_ALL_SIDE_BAR} />
        </Button>
        <Button
          color="primary"
          variant="contained"
          size="large"
          onClick={priceCall}
          className={classes.continueButton}
          id="tubes-continue-button"
          data-testid="tubes-continue-button"
        >
          <FormattedMessage {...messages.CONF_AQUA_CONTINUE_BUTTON} />
        </Button>
        <ViewAllDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          viewAllItems={sessionData}
          setActive={setActive}
          onDelete={deleteOligoItem}
          productName={productType}
        />
        {priceLoading && <CircleLoader />}
        {validationLoading && <CircleLoader />}
      </div>
    )
  } else if (productType !== TubeProduct.Wellred)
    return (
      <>
        <SequenceInfo
          productDetails={productDetails}
          setSequencePreviewData={setSequencePreviewData}
          specificationsData={specificationsData}
        />

        <OptionalModifications
          modificationResponseData={specificationsData}
          productDetails={productDetails}
        />
        <SequenceProperties
          productDetails={productDetails}
          sequencePreviewData={sequencePreviewData}
        />
        {specificationComponent()}
        <ViewAllDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          viewAllItems={sessionData}
          setActive={setActive}
          onDelete={deleteOligoItem}
          productName={productType}
        />
        {priceLoading && <CircleLoader />}
        {validationLoading && <CircleLoader />}
      </>
    )
  else
    return (
      <>
        <SequenceInfo />
        <OptionalModifications
          modificationResponseData={specificationsData}
          productDetails={productDetails}
        />
        <SequenceProperties
          productDetails={productDetails}
          sequencePreviewData={sequencePreviewData}
        />
        <Specifications
          specificationsData={specificationsData}
          productDetails={productDetails}
        />
        <Button
          color="primary"
          variant="contained"
          size="large"
          onClick={priceCall}
        >
          <FormattedMessage {...messages.CONF_CONFIRM_ORDER} />
        </Button>
      </>
    )
}

export default SingleProductEntry
